import { buyRequestsPath } from '@/lib/buyRequestTabs';
import { useEffect, useRef, useState } from 'react';
import { type SearchSnapshot, type HandoffConstraint, describeSnapshot, mergePinned, pinnedConstraints, snapshotHasCriteria, snapshotToRequestFields } from '@/lib/searchHandoff';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FileUp, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { VoiceTextarea } from '@/components/ui/voice-textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { AIThinkingLabel } from '@/components/ai/AIThinkingLabel';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { useBuyerRequests } from '@/hooks/useBuyerRequests';
import { useEntitlements } from '@/hooks/useEntitlements';
import { supabase } from '@/integrations/supabase/client';
import { GRAMS_PER_LB } from '@/lib/buyRequestParser';
import { MultiSelectFilter, type MultiSelectGroup } from '@/components/ui/multi-select-filter';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import InviteSupplierDialog from '@/components/buyer/InviteSupplierDialog';
import LoadCustomerWantsDialog from '@/components/buyer/LoadCustomerWantsDialog';
import { useMatchedDemand } from '@/components/buyer/MatchedDemandList';
import { clearCue } from '@/lib/spotlightCue';
import { PRODUCT_FORM_CATEGORIES, PRODUCT_FORM_ITEMS, getAllFilterableProductForms, getProductFormLabel } from '@/lib/productFormData';
import { EPP_ENVIRONMENT_CODES, EPP_PRODUCT_CODES, eppProductToFormValue } from '@/lib/eppUtils';

/** Parsed EPP product values (e.g. "a/b mix") become the matching product type ("a/b flower mix"). */
const toFormValue = (v: string) => {
  const epp = Object.keys(EPP_PRODUCT_CODES).find((k) => k.toLowerCase() === v.toLowerCase());
  return (epp && eppProductToFormValue(epp)) || v;
};

export const FEATURE_META = {
  id: 'create-buy-request',
  label: 'Make a Buy Request',
  description:
    'Describe what you need in one sentence; Cannafax reads it like a search, checks the supplier menus you already have and shows what fits.',
  triggerType: 'dialog' as const,
  personas: ['buyer', 'buyer_seller', 'seller'] as Array<'buyer' | 'buyer_seller' | 'seller'>,
};

const SPECIES_LABEL: Record<string, string> = { indica: 'Indica', sativa: 'Sativa', hybrid: 'Hybrid' };
const PROCESS_VALUES = ['machine trimmed', 'hand trimmed', 'scissor trimmed', 'drum trimmed', 'untrimmed', 'freeze dried', 'hung dried'];
/** Request summary column only knows four grows; the full set lives in the saved search criteria. */
const ENV_COLUMN: Record<string, string> = { indoor: 'indoor', outdoor: 'outdoor', greenhouse: 'greenhouse', hoophouse: 'greenhouse', 'mixed light - hybrid greenhouse': 'mixed_light', mixed_light: 'mixed_light' };
const titleWords = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase());

/** Pick-several fields: the same fields and values the search filters and AI search use. */
const MULTI: Record<string, { label: string; field: string; aliases: string[]; groups: () => MultiSelectGroup[] }> = {
  form: {
    // EPP Product: one selector. Biomass options are the EPP products; the other groups cover
    // forms EPP has no code for (pre-rolls, concentrates, extracts). Saved as product_form.
    label: 'Product', field: 'product_form', aliases: ['product_form', 'meta.epp_product'],
    groups: () => {
      const { grouped } = getAllFilterableProductForms();
      return PRODUCT_FORM_CATEGORIES.map((c) => ({ label: c, options: grouped[c] ?? [] }));
    },
  },
  species: { label: 'Species', field: 'species', aliases: ['species'], groups: () => [{ options: Object.entries(SPECIES_LABEL).map(([value, label]) => ({ value, label })) }] },
  env: { label: 'Environment', field: 'meta.epp_environment', aliases: ['meta.epp_environment', 'environment'], groups: () => [{ options: Object.keys(EPP_ENVIRONMENT_CODES).map((k) => ({ value: k.toLowerCase(), label: k })) }] },
  process: { label: 'Process', field: 'meta.epp_process', aliases: ['meta.epp_process'], groups: () => [{ options: PROCESS_VALUES.map((v) => ({ value: v, label: titleWords(v) })) }] },
  grade: { label: 'Grade', field: 'meta.grade', aliases: ['meta.grade'], groups: () => [{ options: ['aaaa', 'aaa+', 'aaa', 'aa', 'a', 'b', 'c', 'smalls'].map((v) => ({ value: v, label: v === 'smalls' ? 'Smalls' : v.toUpperCase() })) }] },
  treatment: { label: 'Treatment', field: 'meta.treatment', aliases: ['meta.treatment'], groups: () => [{ options: [{ value: 'non-irradiated', label: 'Non-irradiated' }, { value: 'irradiated', label: 'Irradiated' }] }] },
};
/** Region is fixed to where the buyer can trade, so it is never part of the request. */
const FIXED_FIELDS = new Set(['region']);
const TYPING_PARSE_DELAY_MS = 5000;

/** Lab minimums in the expandable section (same fields as the Search sliders). */
const LAB_MINS: Array<{ key: string; label: string; field: string }> = [
  { key: 'cbd', label: 'Min CBD (%)', field: 'cbd_percent' },
  { key: 'terp', label: 'Min terpenes (%)', field: 'terpene_percent' },
];
/** Yes/no requirements: same constraints the Search filter checkboxes emit. */
const TOGGLES: Array<{ key: string; label: string; field: string; op: string }> = [
  { key: 'no_pesticides', label: 'No pesticides', field: 'compliance.no_pesticides', op: 'is_true' },
  { key: 'no_heavy_metals', label: 'No heavy metals', field: 'compliance.no_heavy_metals', op: 'is_true' },
  { key: 'no_mycotoxins', label: 'No mycotoxins', field: 'compliance.no_mycotoxins', op: 'is_true' },
  { key: 'microbials_pass', label: 'Microbials pass', field: 'compliance.microbials_pass', op: 'is_true' },
  { key: 'has_coa', label: 'Has COA', field: 'lab_name', op: 'is_not_null' },
  { key: 'has_images', label: 'Has pictures', field: 'images', op: 'is_not_null' },
];

/** What the search engine understood, already in buy-request units. */
type Understood = {
  productForm: string | null;
  strainQuery: string | null;
  species: string | null;
  environment: string | null;
  minQuantityG: number | null;
  maxPricePerG: number | null;
  minThcPercent: number | null;
  neededBy?: string | null;
  /** The full search-engine constraint list; stored with the request so matching equals Search. */
  constraints?: Constraint[];
};

type Constraint = { field: string; op: string; values?: (string | number | boolean)[]; primary_values?: string[]; source?: string };

/** Which search fields each editable box owns. Editing a box makes the box the authority. */
const EDIT_FIELDS: Record<string, string[]> = {
  strain: ['strain_name'], qty: ['quantity_grams'],
  price: ['price_per_g_canonical'], thc: ['thc_percent'],
  ...Object.fromEntries(LAB_MINS.map((m) => [m.key, [m.field]])),
};
const SHOWN = new Set([
  ...Object.values(EDIT_FIELDS).flat(),
  ...Object.values(MULTI).flatMap((m) => m.aliases),
  ...TOGGLES.map((t) => t.field),
]);
const NEGATIVE = new Set(['not_in', 'neq', 'not_ilike']);
const POSITIVE = new Set(['in', 'eq', 'ilike', 'contains']);
/** Every value the request asks for on one field (all of them, not just the first). */
function valuesFor(cs: Constraint[] | undefined, aliases: string[]): string[] {
  const out: string[] = [];
  for (const c of cs ?? []) {
    if (!aliases.includes(c.field) || !POSITIVE.has(c.op)) continue;
    for (const v of (c.primary_values?.length ? c.primary_values : c.values ?? [])) {
      const x = String(v).replace(/%/g, '').trim().toLowerCase();
      if (x && !out.includes(x)) out.push(x);
    }
  }
  return out;
}
/** Lower bound the request asks for on a numeric field, as text for an input box. */
function minFor(cs: Constraint[] | undefined, field: string): string {
  const c = (cs ?? []).find((x) => x.field === field && ['gt', 'gte', 'eq', 'around', 'between'].includes(x.op));
  const n = Number(c?.values?.[0]);
  return c && Number.isFinite(n) && n > 0 ? String(n) : '';
}
const FIELD_WORD: Record<string, string> = {
  region: 'Region', cbd_percent: 'CBD', total_cannabinoids_percent: 'Total cannabinoids',
  terpene_percent: 'Terpenes', 'meta.epp_product': 'EPP product', 'meta.certifications': 'Certifications',
};
const OP_WORD: Record<string, string> = { gt: 'over', gte: 'at least', lt: 'under', lte: 'at most', between: 'between', around: 'around', not_in: 'not', neq: 'not', not_ilike: 'not' };
function describeExtra(c: Constraint): string {
  const raw = c.field.replace(/^meta\./, '').replace(/_/g, ' ');
  const word = FIELD_WORD[c.field] ?? raw.replace(/\b\w/g, (x) => x.toUpperCase());
  const vals = (c.primary_values?.length ? c.primary_values : c.values ?? []).map((v) => String(v).replace(/%/g, '')).slice(0, 3).join(', ');
  const op = OP_WORD[c.op];
  return [word, op, vals].filter(Boolean).join(' ').trim();
}

const toNum = (v: string) => (v.trim() === '' || isNaN(Number(v)) ? null : Number(v));
const titleCase = (s: string | null) => (s ? s.replace(/\b\w/g, (c) => c.toUpperCase()) : '');

/** The AI reader is the only reader; a failure is shown honestly, never guessed. */
async function understand(text: string): Promise<Understood | null> {
  const { data, error } = await supabase.functions.invoke('parse-buy-request', { body: { text } });
  if (error || !data) {
    console.error('parse-buy-request failed', error);
    return null;
  }
  return data as Understood;
}

export default function CreateBuyRequestDialog({ open, onOpenChange, initialText, initialSearch, onPosted }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** Starting sentence (e.g. from Ask Cannafax); read by the same engine as typing it. */
  initialText?: string;
  /** The search the buyer just ran (text + chips, already understood by the engine). Used as-is: no second AI read. */
  initialSearch?: SearchSnapshot | null;
  onPosted?: () => void;
}) {
  useFeatureRegistration(FEATURE_META);
  const navigate = useNavigate();
  const { create, requests } = useBuyerRequests();
  const { limitOf } = useEntitlements();
  const { data: demand } = useMatchedDemand();
  const [importOpen, setImportOpen] = useState(false);
  const limit = limitOf('buyer.requests');
  const active = requests.filter((r) => r.status === 'active').length;

  // Journey B: right after posting, the request goes to the buyer's own suppliers.
  const [sendRequest, setSendRequest] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [parsedFor, setParsedFor] = useState('');
  const [parsing, setParsing] = useState(false);
  const [u, setU] = useState<Understood | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [multiEdits, setMultiEdits] = useState<Record<string, string[]>>({});
  const [toggleEdits, setToggleEdits] = useState<Record<string, boolean>>({});
  const [advancedOpen, setAdvancedOpen] = useState<boolean | null>(null);
  const [recurring, setRecurring] = useState(false);
  const seq = useRef(0);
  const pinnedRef = useRef<HandoffConstraint[]>([]);

  // One reader per sentence. Every trigger (5s pause, Enter, mic stop, retry)
  // goes through `runParse`, which claims the sentence in refs (never render
  // closures) so a sentence already being read or already understood is never
  // read again — no matter how many triggers fire.
  const readingRef = useRef('');
  const understoodRef = useRef('');
  const runParse = async (value: string) => {
    const t = value.trim();
    if (t.length < 3 || t === understoodRef.current || t === readingRef.current) return;
    readingRef.current = t;
    const mine = ++seq.current;
    setParsing(true);
    const result = await understand(t);
    if (mine !== seq.current) return; // a newer sentence superseded this one
    readingRef.current = '';
    setParsing(false);
    if (!result) {
      // Leave the request unread (Post stays off) so nothing is posted half-understood.
      toast.error("We couldn't read that request just now", {
        description: 'Please try again in a moment, or edit the sentence.',
        action: { label: 'Try again', onClick: () => void runParse(textRef.current) },
      });
      return;
    }
    // Filter chips from the originating search stay in force when the sentence is re-read.
    setU(pinnedRef.current.length
      ? { ...result, constraints: mergePinned(result.constraints ?? [], pinnedRef.current) as Constraint[] }
      : result);
    setEdits({}); setMultiEdits({}); setToggleEdits({}); // the sentence is the source of truth; re-read it fresh
    understoodRef.current = t;
    setParsedFor(t);
  };

  // Typed/pasted input waits for a calm 5s pause (or Enter) so mid-sentence
  // pauses don't fire the reader. Spoken input is read the moment the mic
  // stops; the pause timer then finds the sentence already claimed.
  const [transcribing, setTranscribing] = useState(false);
  const textRef = useRef(text);
  textRef.current = text;
  const runParseRef = useRef(runParse);
  runParseRef.current = runParse;
  useEffect(() => {
    if (!open || importOpen || transcribing || text.trim().length < 3) return;
    const id = setTimeout(() => void runParseRef.current(textRef.current), TYPING_PARSE_DELAY_MS);
    return () => clearTimeout(id);
  }, [text, transcribing, open, importOpen]);
  const onTranscribingChange = (busy: boolean) => {
    setTranscribing(busy);
    if (!busy) setTimeout(() => void runParseRef.current(textRef.current), 0);
  };

  const stale = text.trim() !== parsedFor;
  const field = (k: string, fallback: string) => (k in edits ? edits[k] : fallback);
  const lbs = u?.minQuantityG ? String(+(u.minQuantityG / GRAMS_PER_LB).toFixed(1)) : '';
  const perLb = u?.maxPricePerG ? String(Math.round(u.maxPricePerG * GRAMS_PER_LB)) : '';
  const multi = (k: string) => {
    if (multiEdits[k]) return multiEdits[k];
    const vals = valuesFor(u?.constraints, MULTI[k].aliases);
    return k === 'form' ? [...new Set(vals.map(toFormValue))] : vals;
  };
  const neededBy = field('neededBy', u?.neededBy ?? '');
  const today = new Date().toISOString().slice(0, 10);
  const extras = (u?.constraints ?? []).filter((c) => !FIXED_FIELDS.has(c.field) && (!SHOWN.has(c.field) || NEGATIVE.has(c.op)));
  const constraintsToSave = () => {
    const owned = new Set([
      ...Object.keys(edits).flatMap((k) => EDIT_FIELDS[k] ?? []),
      ...Object.keys(multiEdits).flatMap((k) => MULTI[k].aliases),
    ]);
    const kept = (u?.constraints ?? []).filter((c) => !FIXED_FIELDS.has(c.field) && !(owned.has(c.field) && !NEGATIVE.has(c.op)));
    for (const [k, vals] of Object.entries(multiEdits)) {
      if (vals.length) kept.push({ field: MULTI[k].field, op: 'in', values: vals, source: 'filter_chip' });
    }
    // Edited lab minimums become the same numeric constraints the Search sliders send.
    for (const [k, f] of [['thc', 'thc_percent'], ...LAB_MINS.map((m) => [m.key, m.field])]) {
      const n = k in edits ? toNum(edits[k]) : null;
      if (n !== null) kept.push({ field: f, op: 'gte', values: [n], source: 'numeric' });
    }
    for (const t of TOGGLES) {
      if (!(t.key in toggleEdits)) continue;
      const i = kept.findIndex((c) => c.field === t.field);
      if (i >= 0) kept.splice(i, 1);
      if (toggleEdits[t.key]) kept.push({ field: t.field, op: t.op, source: 'filter_chip' });
    }
    return kept;
  };
  const toggleOn = (t: (typeof TOGGLES)[number]) =>
    t.key in toggleEdits ? toggleEdits[t.key] : (u?.constraints ?? []).some((c) => c.field === t.field && c.op === t.op);
  const advancedCount =
    multi('grade').length + multi('treatment').length + extras.length +
    (field('thc', u?.minThcPercent?.toString() ?? '') ? 1 : 0) +
    LAB_MINS.filter((m) => field(m.key, minFor(u?.constraints, m.field))).length +
    TOGGLES.filter(toggleOn).length;
  // Opens by itself when the request already asks for lab or quality details.
  const showAdvanced = advancedOpen ?? advancedCount > 0;

  // A starting sentence is read like typing it: the debounce effect parses it.
  useEffect(() => {
    if (!open || textRef.current) return;
    if (initialSearch && snapshotHasCriteria(initialSearch)) {
      // The search engine already understood this search; adopt its constraints directly.
      const t = describeSnapshot(initialSearch);
      pinnedRef.current = pinnedConstraints(initialSearch.constraints);
      setU({ ...snapshotToRequestFields(initialSearch), neededBy: null, constraints: initialSearch.constraints as Constraint[] });
      understoodRef.current = t.trim();
      setParsedFor(t.trim());
      setText(t);
      return;
    }
    if (initialText) setText(initialText);
  }, [open, initialText, initialSearch]);

  const reset = () => {
    seq.current++;
    pinnedRef.current = [];
    readingRef.current = ''; understoodRef.current = '';
    setText(''); setParsedFor(''); setU(null); setEdits({}); setMultiEdits({}); setToggleEdits({}); setAdvancedOpen(null); setRecurring(false); setParsing(false);
  };

  const submit = async () => {
    const qtyLb = toNum(field('qty', lbs));
    const priceLb = toNum(field('price', perLb));
    try {
      const res = await create.mutateAsync({
        rawText: text,
        strainQuery: field('strain', u?.strainQuery ?? '') || null,
        // Summary columns hold the first choice for cards; matching uses every choice in parsedQuery.
        productForm: multi('form')[0] ?? null,
        // Summary column holds only botanical species; profiles like "CBD" stay in parsedQuery.
        species: multi('species').find((s) => s in SPECIES_LABEL) ?? null,
        minQuantityG: qtyLb !== null ? Math.round(qtyLb * GRAMS_PER_LB) : null,
        maxPricePerG: priceLb !== null ? priceLb / GRAMS_PER_LB : null,
        minThcPercent: toNum(field('thc', u?.minThcPercent?.toString() ?? '')),
        environment: multi('env').map((e) => ENV_COLUMN[e]).find(Boolean) ?? null,
        neededBy: neededBy || null,
        isRecurring: recurring,
        parsedQuery: { constraints: constraintsToSave() },
      });
      // The job is done: closing the dialog must not revive the "Make a Buy Request" glow.
      clearCue(window.location.pathname);
      toast.success('Buy Request made', {
        description: res.matches
          ? `${res.matches} matching listing${res.matches === 1 ? '' : 's'} on your supplier menus.`
          : "Nothing matches yet — Your Next Move™ will watch your supplier menus and let you know as soon as new inventory is available.",
        action: { label: 'View', onClick: () => navigate(buyRequestsPath('mine')) },
      });
      const posted = text.trim();
      reset();
      onPosted?.();
      onOpenChange(false);
      // Only cold-start buyers (no supplier menus yet) are asked to send the request out.
      let hasSupplierMenus = true;
      try {
        const { data: me } = await supabase.from('users').select('company_id').eq('id', (await supabase.auth.getUser()).data.user?.id ?? '').maybeSingle();
        if (me?.company_id) {
          const { count } = await supabase.from('company_menu_subscribers').select('menu_id', { count: 'exact', head: true }).eq('subscriber_company_id', me.company_id).is('revoked_at', null);
          hasSupplierMenus = (count ?? 0) > 0;
        }
      } catch { hasSupplierMenus = true; }
      if (!hasSupplierMenus) setSendRequest(posted);
    } catch (e) {
      // Database errors arrive as plain objects, not Error instances.
      const msg = String((e as { message?: unknown })?.message ?? '');
      console.error('create_buyer_request failed', e);
      if (msg.includes('buyer_request_limit')) {
        toast.error('Your plan includes 1 active buy request', {
          description: 'Close or mark your current request fulfilled, or upgrade to Buyer Pro for unlimited.',
          action: { label: 'See plans', onClick: () => navigate('/pricing') },
        });
      } else if (msg.includes('needed_by_in_past')) {
        toast.error('That date has already passed', { description: 'Pick a needed-by date from today onward.' });
      } else {
        toast.error("We couldn't post that request", { description: 'Please try again in a moment.' });
      }
    }
  };

  const renderMulti = (k: string) => {
    const m = MULTI[k];
    const value = multi(k);
    const groups = m.groups();
    const known = new Set(groups.flatMap((g) => g.options.map((o) => o.value)));
    const unknown = value.filter((v) => !known.has(v));
    const allGroups = unknown.length ? [...groups, { label: 'From your request', options: unknown.map((v) => ({ value: v, label: titleWords(v) })) }] : groups;
    const labelOf = (v: string) => (k === 'form' ? getProductFormLabel(v) : titleWords(v));
    return (
      <div key={k} className="space-y-1 min-w-0">
        <Label>{m.label}</Label>
        <MultiSelectFilter
          label={value.length ? value.map(labelOf).join(', ') : 'Any'}
          value={value}
          onChange={(next) => setMultiEdits((prev) => ({ ...prev, [k]: next ?? [] }))}
          groups={allGroups}
          disabled={parsing}
          triggerClassName="w-full justify-between font-normal"
          contentClassName="w-[min(18rem,calc(100vw-2rem))]"
        />
      </div>
    );
  };

  return (
    <>
    {!importOpen && <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-2xl lg:max-w-3xl flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>What are you looking for?</DialogTitle>
          <DialogDescription>
            Say it the way you'd text a supplier. We'll check the menus you already have, and Your Next Move™ will let you know as soon as new inventory is available.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overlay-scroll flex-1 min-h-0 -mx-1 px-1">

          <VoiceTextarea
            data-cue="request-prompt"
            data-cue-next="request-submit"
            data-cue-return="make-buy-request"
            autoFocus
            value={text}
            onValueChange={setText}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // Enter reads now; Shift+Enter keeps a newline.
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void runParse(text); }
            }}
            onTranscribingChange={onTranscribingChange}
            // Never lock the box while reading: edits simply supersede the read
            // in flight (seq guard) and Post stays off until the latest text is read.
            placeholder="e.g. 50 lbs indoor Gelato indica smalls under $600/lb, 25%+ THC"
            rows={3}
            maxLength={2000}
          />

          {demand?.persona === 'power_trader' && (
            <Button type="button" variant="quiet" size="sm" data-request-import disabled={parsing || create.isPending || transcribing}
              onClick={() => setImportOpen(true)}>
              <FileUp className="w-4 h-4" /> Import multiple
            </Button>
          )}

          {parsing && (
            <div className="text-sm text-muted-foreground">
              <AIThinkingLabel context="search" title="Reading your request…" />
            </div>
          )}

          {u && (
            <div className={`space-y-5 transition-opacity ${parsing ? 'opacity-50' : ''}`}>
              <section aria-labelledby="br-core" className="space-y-3">
                <h3 id="br-core" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">What you need</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="space-y-1 min-w-0">
                    <Label htmlFor="br-strain">Strain</Label>
                    <Input id="br-strain" value={field('strain', u.strainQuery ?? '')} onChange={(e) => setEdits({ ...edits, strain: e.target.value })} placeholder="Any" />
                  </div>
                  {renderMulti('species')}
                  {renderMulti('form')}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="space-y-1 min-w-0">
                    <Label htmlFor="br-qty">At least (lbs)</Label>
                    <Input id="br-qty" inputMode="decimal" value={field('qty', lbs)} onChange={(e) => setEdits({ ...edits, qty: e.target.value })} placeholder="Any" />
                  </div>
                  <div className="space-y-1 min-w-0">
                    <Label htmlFor="br-price">Max price ($/lb)</Label>
                    <Input id="br-price" inputMode="decimal" value={field('price', perLb)} onChange={(e) => setEdits({ ...edits, price: e.target.value })} placeholder="Any" />
                  </div>
                  <div className="space-y-1 min-w-0">
                    <Label htmlFor="br-needed">Needed by</Label>
                    <Input id="br-needed" type="date" min={today} value={neededBy} onChange={(e) => setEdits({ ...edits, neededBy: e.target.value })} />
                  </div>
                </div>
              </section>

              {(() => {
                const forms = multi('form');
                const biomass = forms.filter((v) => PRODUCT_FORM_ITEMS.find((i) => i.value === v.toLowerCase())?.category === 'Biomass');
                const unknownForms = forms.filter((v) => !PRODUCT_FORM_ITEMS.some((i) => i.value === v.toLowerCase()));
                const showEpp = forms.length === 0 || biomass.length > 0 || unknownForms.length > 0;
                if (!showEpp) {
                  return (
                    <p className="text-xs text-muted-foreground rounded-md bg-muted px-3 py-2">
                      Growing and processing details apply to flower and biomass, so they're not needed for these products.
                    </p>
                  );
                }
                return (
                  <section aria-labelledby="br-epp" className="space-y-3">
                    <h3 id="br-epp" className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Growing environment, product and processing</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {renderMulti('env')}
                      <div className="space-y-1 min-w-0">
                        <Label className="text-muted-foreground">Product</Label>
                        <div aria-readonly="true" title="Set by Product above" className="flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground">
                          <span className="truncate">{biomass.length ? biomass.map(getProductFormLabel).join(', ') : 'Any'}</span>
                        </div>
                      </div>
                      {renderMulti('process')}
                    </div>
                  </section>
                );
              })()}


              <Collapsible open={showAdvanced} onOpenChange={setAdvancedOpen} className="rounded-md border border-border">
                <CollapsibleTrigger asChild>
                  <Button variant="quiet" size="content" type="button" className="justify-start flex w-full items-center justify-between gap-2 px-3 py-2.5 text-sm font-medium text-foreground">
                    <span>Lab &amp; quality{advancedCount > 0 && <span className="num text-muted-foreground"> ({advancedCount})</span>}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 border-t border-border px-3 pb-3 pt-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="space-y-1 min-w-0">
                      <Label htmlFor="br-thc">Min THC (%)</Label>
                      <Input id="br-thc" inputMode="decimal" value={field('thc', u.minThcPercent?.toString() ?? '')} onChange={(e) => setEdits({ ...edits, thc: e.target.value })} placeholder="Any" />
                    </div>
                    {LAB_MINS.map((m) => (
                      <div key={m.key} className="space-y-1 min-w-0">
                        <Label htmlFor={`br-${m.key}`}>{m.label}</Label>
                        <Input id={`br-${m.key}`} inputMode="decimal" value={field(m.key, minFor(u.constraints, m.field))} onChange={(e) => setEdits({ ...edits, [m.key]: e.target.value })} placeholder="Any" />
                      </div>
                    ))}
                    {(['grade', 'treatment'] as const).map((k) => renderMulti(k))}
                  </div>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Must have</legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
                      {TOGGLES.map((t) => (
                        <label key={t.key} className="flex items-center gap-2 text-sm">
                          <Checkbox checked={toggleOn(t)} disabled={parsing} onCheckedChange={(v) => setToggleEdits((p) => ({ ...p, [t.key]: v === true }))} />
                          {t.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  {extras.length > 0 && (
                    <div className="space-y-1">
                      <Label>Also matching</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {extras.map((c, i) => (
                          <span key={i} className="max-w-full truncate rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs text-foreground">{describeExtra(c)}</span>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">Change these by rewording your request.</p>
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="br-recurring" className="font-normal leading-snug">
              I buy this regularly — keep it open after it's filled
            </Label>
            <Switch id="br-recurring" checked={recurring} onCheckedChange={setRecurring} />
          </div>

          {limit !== null && (
            <p className="text-xs text-muted-foreground">
              {active} of {limit} active request{limit === 1 ? '' : 's'} used on your plan. Buyer Pro has no limit.
            </p>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t border-border pt-3">
          <Button variant="quiet" className="w-full sm:w-auto" onClick={() => onOpenChange(false)}>Not now</Button>
          <Button data-cue="request-submit" className="w-full sm:w-auto" onClick={submit} disabled={text.trim().length < 3 || parsing || stale || create.isPending}>
            {create.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Make a Buy Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>}
    {importOpen && <LoadCustomerWantsDialog
      open={open && importOpen}
      onBack={() => setImportOpen(false)}
      onOpenChange={(o) => {
        if (!o) { setImportOpen(false); reset(); onOpenChange(false); }
      }}
    />}
    <InviteSupplierDialog open={sendRequest !== null} onOpenChange={(o) => { if (!o) setSendRequest(null); }} requestText={sendRequest} />
    </>
  );
}
